import {TheRaveLayers, Layers} from "../view/TheRaveLayers";
import {TheRaveSymbol} from "../symbol/TheRaveSymbol";
import {ReelState} from "../../core/reel/ReelState";
import {TheRave} from "../TheRave";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {GuiToggleButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/GuiToggleButton";
import {PointerStateColorSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateColorSet";
import {PointerStateIconSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateIconSet";
import {IconToggleButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/IconToggleButton";
import {Icon} from "@nolimitcity/slot-launcher/bin/gui/displayobjects/Icon";
import {BetBoostData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetHandler";
import {SvgLoader} from "@nolimitcity/slot-launcher/bin/loader/SvgLoader";
import {GuiDefaultTextures} from "@nolimitcity/slot-launcher/bin/gui/default/GuiDefaultTextures";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";
import {gsap, TimelineLite, TweenLite, Power3, TweenMax} from "gsap";
import {TheRaveSound} from "../sound/TheRaveSound";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class TheRaveReelView {
    public static SYMBOL_WIDTH: number = 144;
    public static SYMBOL_HEIGHT: number = 150;
    public static REEL_TOP: number = 192;
    public static REEL_HEIGHT: number = 150 * 3;
    public static REEL_WIDTH: number = 720;
    public static REEL_LEFT: number = 73.5;

    public static DIMMER_ALPHA_FOR_WIN: number[] = [0.3, 0.4, 0.5, 0.6];
    public static REEL_AREAS: PIXI.Rectangle[] = [
        new PIXI.Rectangle(TheRaveReelView.REEL_LEFT, TheRaveReelView.REEL_TOP, TheRaveReelView.SYMBOL_WIDTH, TheRaveReelView.REEL_HEIGHT),
        new PIXI.Rectangle(216.5, TheRaveReelView.REEL_TOP, TheRaveReelView.SYMBOL_WIDTH, TheRaveReelView.REEL_HEIGHT),
        new PIXI.Rectangle(359, TheRaveReelView.REEL_TOP, TheRaveReelView.SYMBOL_WIDTH, TheRaveReelView.REEL_HEIGHT),
        new PIXI.Rectangle(503, TheRaveReelView.REEL_TOP, TheRaveReelView.SYMBOL_WIDTH, TheRaveReelView.REEL_HEIGHT),
        new PIXI.Rectangle(646, TheRaveReelView.REEL_TOP, TheRaveReelView.SYMBOL_WIDTH, TheRaveReelView.REEL_HEIGHT),
        new PIXI.Rectangle(TheRaveReelView.REEL_LEFT, -3000, TheRaveReelView.REEL_WIDTH, TheRaveReelView.SYMBOL_HEIGHT),
        new PIXI.Rectangle(216.5, -3000, TheRaveReelView.REEL_WIDTH, TheRaveReelView.SYMBOL_HEIGHT),
        new PIXI.Rectangle(359, -3000, TheRaveReelView.REEL_WIDTH, TheRaveReelView.SYMBOL_HEIGHT),
        new PIXI.Rectangle(503, -3000, TheRaveReelView.REEL_WIDTH, TheRaveReelView.SYMBOL_HEIGHT),
        new PIXI.Rectangle(646, -3000, TheRaveReelView.REEL_WIDTH, TheRaveReelView.SYMBOL_HEIGHT)
    ]

    public reelId: number;
    public glow: TimelineSprite;
    public xBetReelFrame: TimelineSprite;
    public spotlight: PIXI.spine.Spine;
    public frame: PIXI.Sprite;
    public mask: PIXI.Graphics | null;
    public allSymbols: TheRaveSymbol[] = [];
    protected readonly _highContainer: PIXI.Container;
    private readonly _highWinContainer: PIXI.Container;
    // TODO: have more layers for sticky symbols and so on.

    public wantBounce: boolean = false;
    public spinStepOffset: number = 0;
    protected _lastStepOffset: number;
    public nextSpinStepConfig: {
        reelState: ReelState,
        duration?: number,
        ease?: string
    } | null;
    protected hasStopped: boolean;
    protected _symbolsNumTop: number;
    protected _stepDuration: number;
    protected _stepEase: string = "none";
    protected _currentTween: TweenLite;
    protected _positionsSnapshot: number[];
    protected _isPositionSnapshotDirty: boolean = true;
    private betBoostAllowed: boolean = false;
    public betBoostPaused: boolean = false;
    public boostButton: GuiToggleButton;
    private _currentTl: TimelineLite;
    private idleAnimSprite: PIXI.Sprite;
    private isBoostButtonEnabled: boolean = false;

    public get stepDistance(): number {
        return this.allSymbols[this._symbolsNumTop].symbolStep;
    }

    public get currentStepLeftTimeOld(): number {
        if (this._currentTween != null && this._currentTween.isActive()) {
            return this._currentTween.duration() * (1 - this._currentTween.progress());
        }
        return 0;
    }

    public get currentStepLeftTime(): number {
        if (this._currentTl != null && this._currentTl.isActive()) {
            return this._currentTl.duration() * (1 - this._currentTl.progress());
        }
        return 0;
    }

    public get currentStepDurationOld(): number {
        if (this._currentTween) {
            return this._currentTween.duration();
        }
        return 0;
    }

    constructor(reelId: number) {
        this.reelId = reelId;
        this._highContainer = this.createSymbolContainer(reelId);
        TheRaveLayers.getLayer(Layers.HIGH_SYMBOLS)?.addChild(this._highContainer);
        this.mask = this.drawMask(1);
        this._highContainer.addChild(this.mask);
        this._highContainer.mask = this.mask;
        this._highWinContainer = this.createSymbolContainer(reelId);
        TheRaveLayers.getLayer(Layers.HIGH_SYMBOLS_WIN)?.addChild(this._highWinContainer);
        const mask: PIXI.Graphics = this.drawMask(1);
        this._highWinContainer.addChild(mask);
        this._highWinContainer.mask = mask;
        this.init();
    }

    private init(): void {
        if (this.reelId === 1) {
            const allowedFeatures = TheRave.apiPlugin.betFeatureController.getAllowedFeatures();
            for (let feature of allowedFeatures){
                if (feature.name == "BOOSTED_BET"){
                    this.betBoostAllowed = true;
                }
            }
            if (this.betBoostAllowed) {
                this.createBoostButton();
            }
            TheRave.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, () => this.onBetFeature());
        }
    }

    private onBetFeature() {
        const active = TheRave.apiPlugin.betFeatureController.getActiveBetFeature();

        if (active?.name == "BOOSTED_BET") {
                this.showBoostFrame();
                this.boostButton.toggled = true;
        } else {
            this.hideBoostFrame();
            this.boostButton.toggled = false;
        }
    }

    private setReelState(reelState: ReelState): void {
        if (reelState) {
            if (this.reelId > 4) {
                TheRave.enhancerReels[this.reelId - 5].reelState = reelState;
            } else {
                TheRave.reels[this.reelId].reelState = reelState;
            }
        }
    }

    private setupIconSet(set: number): Icon [] {
        let iconSet: Icon [] = [];
        if (set === 1) {
            iconSet = [
                new Icon(ImgLoader.getImgTexture("xBetActive")),
                new Icon(ImgLoader.getImgTexture("xBetActive")),
                new Icon(ImgLoader.getImgTexture("xBetActive")),
                new Icon(ImgLoader.getImgTexture("xBetActive"))
            ];

            iconSet.forEach((icon) => {
                const svg: PIXI.Sprite = new PIXI.Sprite(SvgLoader.getSvgTexture(GuiDefaultTextures.X_BET));
                svg.width = 6;
                svg.height = 10;
                svg.anchor.set(0.5, 0.5);
                svg.x = icon.width / 2;
                svg.y = icon.height / 2;
                svg.tint = 0xFFD700;
                //icon.width = 138;
                //icon.height = 50;
                //icon.addChild(svg);
                //icon.scale.set(0.75, 0.75);
            });
        } else if (set === 2) {
            iconSet = [
                new Icon(ImgLoader.getImgTexture("xBetInactive")),
                new Icon(ImgLoader.getImgTexture("xBetInactive")),
                new Icon(ImgLoader.getImgTexture("xBetInactive")),
                new Icon(ImgLoader.getImgTexture("xBetInactive"))
            ];

            iconSet.forEach((icon) => {
                const svg: PIXI.Sprite = new PIXI.Sprite(SvgLoader.getSvgTexture(GuiDefaultTextures.X_BET));
                svg.width = 6;
                svg.height = 10;
                svg.anchor.set(0.5, 0.5);
                svg.x = icon.width / 2;
                svg.y = icon.height / 2;
                //icon.width = 138;
                //icon.height = 50;
                //icon.addChild(svg);
                //icon.scale.set(0.75, 0.75);
            });
        }


        return iconSet;
    }

    private createBoostButton() {
        /*const colorSet:PointerStateColorSet = new PointerStateColorSet(
            0xFFE05194,
            0xFFAF38EB,
            0xFFEB96EB,
            0xCCE05194
        );*/
        const colorSet: PointerStateColorSet = new PointerStateColorSet(
            0xFFFFFFFF,
            0xFFAAAAAA,
            0xFFCCCCCC,
            0xAAFFFFFF
        );

        const iconSet1: Icon[] = this.setupIconSet(1);

        const iconSet2: Icon[] = this.setupIconSet(2);

        this.boostButton = new IconToggleButton("boost",
            new PointerStateIconSet(
                iconSet1[0],
                iconSet1[1],
                iconSet1[2],
                iconSet1[3],
            ),
            colorSet,
            new PointerStateIconSet(
                iconSet2[0],
                iconSet2[1],
                iconSet2[2],
                iconSet2[3]
            ),
            colorSet);

        this.boostButton.addClickCallback(() => this.boostToggle());
        // this.boostButton.toggled = false;
        this.boostButton.enable(true);
        //this.isBoostButtonEnabled = true;
        this.boostButton.position.set(this._highWinContainer.x - (this.boostButton.width / 2), 643);

        TheRaveLayers.getLayer(Layers.REEL_FRAME)!.addChild(this.boostButton);
        //this.bottomView.addChild(this.boostButtonSpine);
    }

    public hideBoostButton() {
        if (!this.boostButton || this.reelId !== 1) {
            return;
        }
        if (this.boostButton.visible) {
            this.boostButton.visible = false;
            this.hideBoostFrame();
        }
    }

    public addAndPlayIdleAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (this.boostButton && this.boostButton && !this.isBoostActive()) {

            let hasIdleAnim: boolean = false;
            let spr: PIXI.DisplayObject | undefined;
            this.boostButton.children.forEach((child) => {
                if (child.name === "idleAnim") {
                    hasIdleAnim = true;
                    //child.visible = true;
                    spr = child;
                }
            });

            if (hasIdleAnim && spr) {
                this.boostButton.removeChild(spr);
                hasIdleAnim = false;
            }

            if (!hasIdleAnim) {
                const idleAnimSprite = new PIXI.Sprite(ImgLoader.getImgTexture("xBetActive"));
                idleAnimSprite.name = "idleAnim";
                idleAnimSprite.alpha = 0;
                this.idleAnimSprite = idleAnimSprite;
                this.boostButton.addChild(idleAnimSprite);
            }

            tl.add(TweenMax.fromTo(this.idleAnimSprite, 0.3, {
                alpha: 0,
                yoyo: true

            }, {
                alpha: 1
            }));
            tl.add(TweenMax.fromTo(this.idleAnimSprite, 0.3, {
                alpha: 1

            }, {
                alpha: 0
            }))
        }

        return tl;
    }

    public showBoostButton() {
        if (!this.boostButton) {
            return;
        }

        if (this.betBoostPaused) {
            return;
        }

        if (!this.boostButton.visible) {
            this.boostButton.visible = true;
            if (this.isBoostActive()) {
                this.showBoostFrame();
            }
        }
    }

    public isBoostActive(): boolean {
        const activeFeature = TheRave.apiPlugin.betFeatureController.getActiveBetFeature();
        return (this.reelId === 1 && activeFeature?.name == "BOOSTED_BET");
    }

    private boostToggle(playSound: boolean = true) {
        if (!this.betBoostAllowed || this.betBoostPaused) {
            return;
        }

        if (!this.boostButton.toggled) {
            TheRave.apiPlugin.betFeatureController.setActiveBetFeature("BOOSTED_BET");
        } else {
            const activeFeature = TheRave.apiPlugin.betFeatureController.getActiveBetFeature();
            if (activeFeature?.name == "BOOSTED_BET"){
                TheRave.apiPlugin.betFeatureController.setActiveBetFeature();
            }

        }
    }

    public showBoostFrame(playSound: boolean = true): void {
        if (!this.xBetReelFrame) {
            this.xBetReelFrame = new TimelineSprite(TheRave.assetLoader.getTextures("xBetReelFrame"));
            TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)!.addChild(this.xBetReelFrame);
            this.xBetReelFrame.anchor.set(0, 0);
            this.xBetReelFrame.position.set(100, 160);
            this.xBetReelFrame.blendMode = PIXI.BLEND_MODES.ADD;
            this.xBetReelFrame.alpha = 0;
        }
        if (playSound) {
            TheRaveSound.playSoundEffect("xBetOn", 0);
        }
        this.xBetReelFrame.getAnimationAutoShowHide(true, false);
        TweenLite.fromTo(this.xBetReelFrame, {alpha: 0}, {alpha: 1, ease: Power3.easeInOut});
    }

    public hideBoostFrame(playSound: boolean = true): void {
        if (this.xBetReelFrame && this.xBetReelFrame.alpha > 0) {
            if (playSound) {
                TheRaveSound.playSoundEffect("xBetOff", 0);
            }
            this.xBetReelFrame.stopLoop();
            TweenLite.fromTo(this.xBetReelFrame, {alpha: 1}, {alpha: 0, ease: Power3.easeInOut});
        }
    }

    public enableButton(value: boolean = false) {

        if (this.reelId === 1) {
            if (!this.betBoostAllowed || this.betBoostPaused) {
                return;
            }
            this.boostButton.enable(value);
            if (value) {
                this.isBoostButtonEnabled = true;
                //this.boostButtonSpine.visible = !this.boostButton.toggled;
            } else {
                this.isBoostButtonEnabled = false;
                //this.boostButtonSpine.visible = value;
            }
        }
    }

    public highLight(): void {
        if (!this.frame) {
            this.frame = new TimelineSprite(TheRave.assetLoader.getTextures("FS_SFS/green"));
            TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)!.addChild(this.frame);
            this.frame.anchor.set(0.5, 0);
            this.frame.position.set(TheRaveReelView.REEL_AREAS[this.reelId].x, 146);
        }
        this.frame.alpha = 0;
        TweenLite.fromTo(this.frame, 0.7, {alpha: 1, delay: 1.2}, {alpha: 0});
    }

    private createSymbolContainer(reelId: number): PIXI.Container {
        const container = new PIXI.Container();
        container.position.set(TheRaveReelView.REEL_AREAS[reelId].x, TheRaveReelView.REEL_AREAS[reelId].y);
        return container;
    }

    public addSymbol(symbol: TheRaveSymbol): void {
        this._highContainer.addChild(symbol);
    }

    public showNoWin(type: number = -1): void {
        const nowinDimmerAlpha: number = type > -1 ? TheRaveReelView.DIMMER_ALPHA_FOR_WIN[type] : 0.6;
        TheRave.fullScreenDimLayer.startReelAreaDimmer(nowinDimmerAlpha);
    }

    public hideNoWin(): void {
        TheRave.fullScreenDimLayer.stopReelAreaDimmer();
    }

    public addWinSymbol(symbol: TheRaveSymbol): void {
        if (!symbol) {
            debugger;
        }
        if (symbol.isFullWild) {
            this._highWinContainer.addChild(symbol)
            return;
        }
        if (symbol.symbolName === "XW") {
            return;
        }
        this._highWinContainer.addChild(symbol);
    }

    public removeSymbol(symbol: TheRaveSymbol): void {
        if (symbol.parent) {
            symbol.parent.removeChild(symbol);
        }
    }

    public initSymbolsPosition(symbolsNumTop: number): void {
        this._symbolsNumTop = symbolsNumTop;
        let offset: number = 0;
        for (let i = symbolsNumTop; i < this.allSymbols.length; i++) {
            this.allSymbols[i].y = offset + this.allSymbols[i].symbolHeight * 0.5;
            offset += this.allSymbols[i].symbolStep;
        }
        if (symbolsNumTop > 0) {
            offset = 0;
            for (let i = symbolsNumTop - 1; i >= 0; i--) {
                this.allSymbols[i].y = offset - this.allSymbols[i].bottomPadding - this.allSymbols[i].symbolHeight * 0.5;
                offset -= this.allSymbols[i].symbolStep;
            }
        }
        this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].position.y);
    }

    private updatePositionSnapshot(): void {

        if (this._isPositionSnapshotDirty) {
            // TODO: Reverting pos switch, due to switching reels
            this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].y);
            this._isPositionSnapshotDirty = false;
            this._lastStepOffset = 0;
        }

    }

    public get currentStepDuration(): number {
        if (this._currentTl) {
            return this._currentTl.duration();
        }
        return 0;
    }

    public step(newSymbol: TheRaveSymbol, removeSymbolCallback: () => void, stepDistance?: number): TimelineLite {
        const reelState: ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration: number = this.nextSpinStepConfig?.duration ? this.nextSpinStepConfig.duration : this._stepDuration;
        const stepEase: string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        if (stepDistance == null) {
            stepDistance = this.stepDistance; // stepDistance is the size of first coming symbol.
        }
        this.nextSpinStepConfig = null;
        this._currentTl = gsap.timeline();
        // start
        this._currentTl.add(() => {
            if (reelState) {
                this.setReelState(reelState);
            }
            this._stepDuration = stepDuration;
            this._stepEase = stepEase;
            this.spinStepOffset = 0;
            this.updatePositionSnapshot();
        });

        // update
        this._currentTl.add(gsap.to(this,
            {
                duration: stepDuration,
                ease: stepEase,
                spinStepOffset: stepDistance,
                onUpdate: () => {
                    //this.updatePositionSnapshot(); // run here because sometimes onUpdate run before onStart.
                    this.allSymbols.forEach((symbol, index) => {
                        symbol.y = this._positionsSnapshot[index] + this.spinStepOffset;
                        // TODO: Reverting pos switch, due to switching reels
                        //symbol.y = this._positionsSnapshot[index];
                    });
                    if (this.wantBounce && this.spinStepOffset < this._lastStepOffset) {
                        this.doBounce();
                        return;
                    }
                    this._lastStepOffset = this.spinStepOffset;
                }
            }
        ));

        // end
        this._currentTl.add(() => {
            this._isPositionSnapshotDirty = true;
            //this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].position.y);
            removeSymbolCallback();
            if (this.wantBounce) {
                this.doBounce();
            }
        });

        return this._currentTl;
    }

    public stepOld(newSymbol: TheRaveSymbol, removeSymbolCallback: () => void, stepDistance?: number): TweenLite {
        const reelState: ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration: number = this.nextSpinStepConfig?.duration ? this.nextSpinStepConfig.duration : this._stepDuration;
        const stepEase: string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        if (stepDistance == null) {
            stepDistance = this.stepDistance; // stepDistance is the size of first coming symbol.
        }
        this.nextSpinStepConfig = null;

        this._currentTween = TweenLite.fromTo(this,
            {spinStepOffset: 0},
            {
                duration: stepDuration,
                ease: stepEase,
                spinStepOffset: stepDistance,
                onStart: () => {
                    if (reelState) {
                        if (this.reelId > 4) {
                            TheRave.enhancerReels[this.reelId - 5].reelState = reelState;
                        } else {
                            TheRave.reels[this.reelId].reelState = reelState;
                        }

                    }
                    this._stepDuration = stepDuration;
                    this._stepEase = stepEase;
                    this.updatePositionSnapshot();
                },
                onUpdate: () => {
                    this.updatePositionSnapshot(); // run here because sometimes onUpdate run before onStart.
                    this.allSymbols.forEach((symbol, index) => {
                        symbol.y = this._positionsSnapshot[index] + this.spinStepOffset;
                        // TODO: Reverting pos switch, due to switching reels
                        //symbol.y = this._positionsSnapshot[index];
                    });
                    if (this.wantBounce && this.spinStepOffset < this._lastStepOffset) {
                        this.doBounce();
                        return;
                    }
                    this._lastStepOffset = this.spinStepOffset;
                },
                onComplete: () => {
                    this._isPositionSnapshotDirty = true;
                    this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].position.y);
                    removeSymbolCallback();
                    if (this.wantBounce) {
                        this.doBounce();
                    }
                }
            });

        return this._currentTween;
    }

    protected doBounce(): void {
        this.wantBounce = false;
        if (this.reelId > 4) {
            TheRave.enhancerReels[this.reelId - 5].reelState = ReelState.BOUNCE;
        } else {
            TheRave.reels[this.reelId].reelState = ReelState.BOUNCE;
        }
    }

    public hide(toHide: boolean): void {
        this.hasStopped = true;
        this._highContainer.visible = !toHide;
    }

    public show(): void {
        this._highContainer.visible = true;
    }

    public liftAboveDimmer(): void {
        TheRaveLayers.getLayer(Layers.HIGH_SYMBOLS)!.removeChild(this._highContainer);
        TheRaveLayers.getLayer(Layers.NEARWIN_REEL)!.addChild(this._highContainer);
    }

    public resetHighContainerLayer(): void {
        TheRaveLayers.getLayer(Layers.NEARWIN_REEL)!.removeChild(this._highContainer);
        TheRaveLayers.getLayer(Layers.HIGH_SYMBOLS)!.addChild(this._highContainer);
    }

    public addToLinkedContainer(): void {
        TheRaveLayers.getLayer(Layers.LINKED_SYMBOLS)!.position.y = 40;
        TheRaveLayers.getLayer(Layers.LINKED_SYMBOLS)!.addChild(this._highContainer);
    }

    public resetMask(linkedNum: number, left: number = 0): void {
        if (this.mask) {
            this._highContainer.removeChild(this.mask);
            this._highContainer.mask = null;
            this.mask = null;
            if (linkedNum == 1) {
                this.allSymbols.forEach((sym: TheRaveSymbol) => sym.removeLinkedSymbols());
            }
        }

        this.mask = this.drawMask(linkedNum, left);
        this._highContainer.addChild(this.mask);
        this._highContainer.mask = this.mask;
        if (linkedNum === 1) {
            TheRaveLayers.getLayer(Layers.HIGH_SYMBOLS)!.addChild(this._highContainer);
        }
    }

    private drawMask(reelNum: number, mostLeftReel: number = 0): PIXI.Graphics {
        const reelId: number = this.reelId,
            left: number = (reelNum === 1 ? 0 : (this.reelId - mostLeftReel) * 144);
        const mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 1);
        mask.drawShape(new PIXI.Rectangle(-TheRaveReelView.REEL_AREAS[reelId].width * 0.5 - left, 0, TheRaveReelView.REEL_AREAS[reelId].width * reelNum, TheRaveReelView.REEL_AREAS[reelId].height));
        mask.endFill();
        mask.name = reelId.toString();
        return mask;
    }
}